
import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Stamp, Award, BookOpen, Phone, Briefcase } from "lucide-react";

const translations = {
  en: {
    title: "Our Services",
    subtitle: "Comprehensive Support for Your Educational Journey",
    services: [
      {
        icon: GraduationCap,
        title: "University Admissions",
        description: "Expert guidance for university applications and program selection"
      },
      {
        icon: Stamp,
        title: "Visa Support",
        description: "Complete assistance with student visa applications and documentation"
      },
      {
        icon: Award,
        title: "Scholarships",
        description: "Access to scholarships up to 50% of tuition fees"
      },
      {
        icon: BookOpen,
        title: "English Test Prep",
        description: "Preparation for IELTS, Duolingo, and Linguaskill tests"
      },
      {
        icon: Phone,
        title: "Free Consultation",
        description: "Personal guidance to plan your educational journey"
      },
      {
        icon: Briefcase,
        title: "Post-Study Work",
        description: "Guidance on post-study work opportunities and visas"
      }
    ]
  },
  ar: {
    title: "خدماتنا",
    subtitle: "دعم شامل لرحلتك التعليمية",
    services: [
      {
        icon: GraduationCap,
        title: "القبول الجامعي",
        description: "إرشادات خبيرة لطلبات الجامعة واختيار البرنامج"
      },
      {
        icon: Stamp,
        title: "دعم التأشيرات",
        description: "مساعدة كاملة في طلبات تأشيرات الطلاب والوثائق"
      },
      {
        icon: Award,
        title: "المنح الدراسية",
        description: "الوصول إلى منح دراسية تصل إلى 50٪ من الرسوم الدراسية"
      },
      {
        icon: BookOpen,
        title: "تحضير اختبار اللغة",
        description: "التحضير لاختبارات IELTS و Duolingo و Linguaskill"
      },
      {
        icon: Phone,
        title: "استشارة مجانية",
        description: "إرشادات شخصية لتخطيط رحلتك التعليمية"
      },
      {
        icon: Briefcase,
        title: "العمل بعد الدراسة",
        description: "إرشادات حول فرص العمل وتأشيرات ما بعد الدراسة"
      }
    ]
  },
  so: {
    title: "Adeegyadeena",
    subtitle: "Taageero Dhammaystiran oo Safarka Waxbarashadaada",
    services: [
      {
        icon: GraduationCap,
        title: "Ogolaanshaha Jaamacadda",
        description: "Hagid khibrad leh oo ku saabsan codsiyada jaamacadda iyo doorashada barnaamijka"
      },
      {
        icon: Stamp,
        title: "Taageerada Fiisaha",
        description: "Caawinta buuxda ee codsiyada fiisaha ardayda iyo dukumentiyada"
      },
      {
        icon: Award,
        title: "Deeqaha Waxbarasho",
        description: "Helitaanka deeqo waxbarasho oo gaara 50% ee lacagta waxbarashada"
      },
      {
        icon: BookOpen,
        title: "Diyaarinta Imtixaanka Ingiriisiga",
        description: "Diyaarinta imtixaanada IELTS, Duolingo, iyo Linguaskill"
      },
      {
        icon: Phone,
        title: "La-talin Bilaash ah",
        description: "Hagid shaqsiyeed si loo qorsheeyo safarka waxbarashadaada"
      },
      {
        icon: Briefcase,
        title: "Shaqada Ka-dib Waxbarashada",
        description: "Hagid ku saabsan fursadaha shaqada iyo fiisaha ka-dib waxbarashada"
      }
    ]
  }
};

function Services({ language }) {
  const content = translations[language];

  return (
    <section className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{content.title}</h2>
          <p className="text-lg text-muted-foreground">{content.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {content.services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-background rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="bg-primary/10 rounded-full p-3 w-12 h-12 flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
              <p className="text-muted-foreground">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Services;
